import { styled } from '@mui/material';
import { SnackbarProvider, MaterialDesignContent } from 'notistack';
import { ReactNode } from 'react';
import ClipReadySnackBar from './ClipReadySnackbar';
import ErrorWithDetailsSnackbar from './ErrorWithDetailsSnackbar';
import Fade from '../transitions/Fade';

type Props = {
  children: ReactNode;
};

// Shared base styles for all snackbar variants
export const baseSnackbarStyles = {
  backdropFilter: 'blur(8px)', // Subtle blur effect
  boxShadow: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 400,
  padding: '12px 16px',
};

// Create variant-specific styles with colored tint background and border
// rgb: [r, g, b] values for the variant color
export const createVariantStyles = (rgb: [number, number, number]) => ({
  ...baseSnackbarStyles,
  backgroundColor: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.1)`, // Subtle tint
  border: `0.5px solid rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.2)`,
  '& svg': {
    fill: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.9)`,
    opacity: 0.9,
  },
});

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent-success': createVariantStyles([18, 183, 106]), // Green
  '&.notistack-MuiContent-error': createVariantStyles([240, 68, 56]), // Red
  '&.notistack-MuiContent-warning': createVariantStyles([253, 176, 34]), // Orange
  '&.notistack-MuiContent-info': createVariantStyles([33, 150, 243]), // Blue
  '&.notistack-MuiContent-default': createVariantStyles([219, 219, 225]), // Gray (subtle)
}));

// Custom styled variant for clip reset notifications (uses shared base with custom accent)
const ClipResetSnackbar = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent': {
    ...baseSnackbarStyles,
    backgroundColor: 'rgba(99, 102, 241, 0.2)', // Indigo tint
    border: '0.5px solid rgba(99, 102, 241, 0.4)', // Indigo border
    fontWeight: 500, // Slightly bolder for emphasis
    '& svg': {
      fill: 'rgba(99, 102, 241, 0.9)', // Indigo icon to match border
      opacity: 0.9,
    },
  },
}));

const StyledSnackbarProvider = ({ children }: Props) => {
  return (
    <SnackbarProvider
      TransitionComponent= {Fade}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      hideIconVariant
      Components={{
        success: StyledMaterialDesignContent,
        error: StyledMaterialDesignContent,
        info: StyledMaterialDesignContent,
        warning: StyledMaterialDesignContent,
        default: StyledMaterialDesignContent,
        clipReady: ClipReadySnackBar,
        errorWithDetails: ErrorWithDetailsSnackbar,
        clipReset: ClipResetSnackbar,
      }}
      autoHideDuration={5000}
    >
      <>{children}</>
    </SnackbarProvider>
  );
};

export default StyledSnackbarProvider;
